import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import client, { imgUrl, assetUrl } from "@/lib/glossyApi";
import MagazineLayout from "@/components/MagazineLayout";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { cycleIndexFor, editionLabel } from "@/lib/issue";

const SECTIONS = [
  { key: "features", title: "The Features", kicker: "Cover-grade editorial", dark: false, ratio: "aspect-[3/4]" },
  { key: "spotlights", title: "The Spotlights", kicker: "Across the UK", dark: false, ratio: "aspect-[4/3]" },
  { key: "edit", title: "The Edit", kicker: "Member stories", dark: false, ratio: "aspect-[3/4]" },
  { key: "newsroom", title: "The Newsroom", kicker: "Written by S/ME", dark: true, ratio: "aspect-[3/4]" },
  { key: "interviews", title: "The Interviews", kicker: "In their own words", dark: false, ratio: "aspect-[4/5]" },
  { key: "desk", title: "Members' Desk", kicker: "Columns", dark: false, ratio: "aspect-[4/3]" },
];

export default function ArchiveEdition() {
  const { key } = useParams();
  const [ed, setEd] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    client.get(`/archive/editions/${key}`).then((r) => setEd(r.data)).catch(() => setMissing(true));
  }, [key]);

  const label = ed ? (ed.issue ? editionLabel(cycleIndexFor(ed.issue + "T12:00:00Z")) : "The Test Edition · August 2026") : "";
  const issueNo = ed?.issue ? cycleIndexFor(ed.issue + "T12:00:00Z") + 1 : 0;
  const cover = ed?.cover;
  const allArts = ed ? SECTIONS.flatMap((s) => ed.sections[s.key] || []) : [];

  if (missing) {
    return (
      <MagazineLayout>
        <div className="pt-[68px] bg-cream min-h-screen flex items-center justify-center" data-testid="archive-edition-missing">
          <div className="text-center px-5">
            <h1 className="font-serif text-4xl tracking-tight">That edition isn't in the archive.</h1>
            <Link to="/archive" className="inline-flex items-center gap-2 mt-6 overline link-underline">Back to the Archive <ArrowUpRight size={13} /></Link>
          </div>
        </div>
      </MagazineLayout>
    );
  }

  return (
    <MagazineLayout darkNav>
      {ed && (
        <Seo
          title={`${ed.issue ? `Issue Nº ${String(issueNo).padStart(2, "0")}` : "The Test Edition"} — The S/ME Archive`}
          description={`Read S/ME ${label} in full — ${cover ? `cover star ${cover.member_name || cover.member_business}, ` : ""}${ed.count} stories: spotlights, interviews, The Edit and the Newsroom, exactly as published.`}
          image={cover ? imgUrl(cover) : undefined}
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "PublicationIssue",
            issueNumber: issueNo || "Preview",
            datePublished: ed.issue || "2026-08-01",
            name: `S/ME — ${label}`,
            isPartOf: { "@type": "Periodical", name: "S/ME", alternateName: "See Me" },
            hasPart: allArts.slice(0, 40).map((a) => ({
              "@type": "Article", headline: a.title,
              url: typeof window !== "undefined" ? `${window.location.origin}/read/${a.slug}` : `/read/${a.slug}`,
            })),
          }}
        />
      )}

      {/* HERO — the edition's cover, as it ran */}
      <section className="relative min-h-[85vh] w-full overflow-hidden grain bg-ink flex items-end" data-testid="archive-edition-hero">
        {cover && (cover.cover_video ? (
          <video src={assetUrl(cover.cover_video)} poster={cover.cover_video_poster ? imgUrl({ cover_image: cover.cover_video_poster }) : imgUrl(cover)} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover ed-grade" style={{ objectPosition: cover.hero_focus || "center 40%" }} data-testid="archive-cover-video" />
        ) : (
          <img src={imgUrl(cover)} alt={cover.title} className="absolute inset-0 w-full h-full object-cover ed-grade" style={{ objectPosition: cover.hero_focus || "center 40%" }} data-testid="archive-cover-image" />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/25 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/50" />
        <div className="relative z-10 w-full max-w-[1600px] mx-auto px-5 sm:px-8 pb-16 sm:pb-20 pt-40">
          <Link to="/archive" className="inline-flex items-center gap-2 overline text-cream/70 hover:text-gloss-gold transition-colors mb-8" data-testid="back-to-archive-link">
            <ArrowLeft size={13} /> The Archive
          </Link>
          <p className="overline text-gloss-gold mb-4">From the Archive · {label}</p>
          {cover ? (
            <>
              <h1 className="font-serif text-cream text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-3xl">{cover.title}</h1>
              {cover.dek && <p className="font-sans text-cream/80 text-lg sm:text-xl mt-5 max-w-2xl leading-relaxed">{cover.dek}</p>}
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/60 mt-4">Cover Star · {cover.member_name}{cover.member_business ? ` · ${cover.member_business}` : ""}</p>
              <Link to={`/read/${cover.slug}`} className="inline-flex items-center gap-3 mt-8 bg-cream text-ink px-7 py-4 overline hover:bg-gloss-gold transition-colors" data-testid="archive-cover-cta">
                Read the cover story <ArrowRight size={16} />
              </Link>
            </>
          ) : (
            <h1 className="font-serif text-cream text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-3xl">{label}</h1>
          )}
          <p className="font-sans text-cream/60 text-sm mt-6">{ed?.count || 0} stories in this edition — every one still readable in full.</p>
        </div>
      </section>

      {/* EDITOR'S LETTER — as it ran */}
      {ed?.editors_letter && (
        <section className="bg-cream py-16 sm:py-20 px-5 sm:px-8 border-b border-ink/10" data-testid="archive-editors-letter">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px flex-1 bg-ink/15" />
              <p className="overline text-gloss-silver text-center shrink-0">Editor's Letter · {label}</p>
              <span className="h-px flex-1 bg-ink/15" />
            </div>
            <p className="font-serif text-lg sm:text-xl leading-[1.65] text-ink/90 whitespace-pre-line">
              <span className="float-left font-serif text-6xl sm:text-7xl leading-[0.72] text-gloss-gold mr-3 mt-1.5">{ed.editors_letter.charAt(0)}</span>
              {ed.editors_letter.slice(1)}
            </p>
            <p className="font-serif text-2xl italic tracking-tight text-ink text-right mt-8">The Editors</p>
          </div>
        </section>
      )}

      {/* SECTIONS — the edition, cover to cover */}
      {ed && SECTIONS.map((s) => {
        const arts = ed.sections[s.key] || [];
        if (!arts.length) return null;
        return (
          <section key={s.key} className={`${s.dark ? "bg-ink text-cream grain relative" : "bg-cream"} py-16 sm:py-24 px-5 sm:px-8`} data-testid={`archive-section-${s.key}`}>
            <div className="max-w-[1600px] mx-auto relative z-10">
              <div className={`flex items-end justify-between border-b ${s.dark ? "border-cream/20" : "border-ink/20"} pb-4 mb-10`}>
                <div>
                  <p className={`overline mb-2 ${s.dark ? "text-gloss-gold" : "text-gloss-silver"}`}>{s.kicker}</p>
                  <h2 className="font-serif text-4xl sm:text-6xl tracking-tight leading-none">{s.title}</h2>
                </div>
                <span className={`overline shrink-0 ${s.dark ? "text-cream/50" : "text-ink/40"}`}>{arts.length} {arts.length === 1 ? "story" : "stories"}</span>
              </div>
              <div className={`grid grid-cols-1 sm:grid-cols-2 ${arts.length >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-6`}>
                {arts.map((a, i) => <ArchiveCard key={a.id} a={a} ratio={s.ratio} delay={i * 0.05} />)}
              </div>
            </div>
          </section>
        );
      })}

      {/* FOOT — back to archive */}
      <section className="bg-ink text-cream py-16 px-5 sm:px-8 grain relative">
        <div className="max-w-[1600px] mx-auto relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <p className="font-serif text-3xl sm:text-4xl tracking-tight">That was {label.split("·")[0].trim()}.</p>
          <Link to="/archive" className="inline-flex items-center gap-3 bg-cream text-ink px-7 py-4 overline hover:bg-gloss-gold transition-colors" data-testid="archive-foot-link">
            More back issues <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </MagazineLayout>
  );
}

function ArchiveCard({ a, ratio, delay }) {
  return (
    <Reveal delay={delay}>
      <Link to={`/read/${a.slug}`} className="group block relative overflow-hidden bg-ink" data-testid="archive-article-card">
        <div className={`relative ${ratio} ed-frame`}>
          <img src={imgUrl(a)} alt={a.title} className="w-full h-full object-cover ed-grade transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-5">
          <p className="overline text-gloss-gold">{a.category}</p>
          <h3 className="font-serif text-xl sm:text-2xl text-cream tracking-tight leading-tight mt-2 line-clamp-3">{a.title}</h3>
          {(a.member_name || a.member_business) && (
            <p className="font-sans text-xs text-cream/70 mt-1">{a.member_name}{a.member_business ? ` · ${a.member_business}` : ""}</p>
          )}
        </div>
      </Link>
    </Reveal>
  );
}
